import { PurchaseAssistOffer, PurchaseAssistRequest } from '@prometheus/types'

/**
 * Pure view-model for the "you're a bit short — want me to cover it?" popup.
 * Kept out of the component so the wording and the withdrawal breakdown can
 * be unit tested without a DOM.
 */

export type PurchaseAssistSourceKey = 'bonds' | 'loc' | 'planet_loans'

export interface PurchaseAssistWithdrawal {
  source: PurchaseAssistSourceKey
  label: string
  amount: number
  /**
   * Planet loans can only be drawn at whole-level amounts, so the real
   * withdrawal may overshoot this figure (the excess just stays as cash).
   */
  approximate?: boolean
}

export const SOURCE_LABELS: Record<PurchaseAssistSourceKey, string> = {
  bonds: 'Bonds',
  loc: 'Line of Credit',
  planet_loans: 'Planet Loans',
}

/**
 * Mirrors the server's `raiseCash` order — the player's own savings first,
 * then borrowing, then loans against their planets — so the popup promises
 * exactly what the server will do.
 */
export const withdrawalPlan = (offer: PurchaseAssistOffer): PurchaseAssistWithdrawal[] => {
  let remaining = Math.max(offer.cash_to_raise, 0)
  const plan: PurchaseAssistWithdrawal[] = []
  const order: PurchaseAssistSourceKey[] = ['bonds', 'loc', 'planet_loans']

  for (const source of order) {
    if (remaining <= 0) {
      break
    }
    const available = Math.max(offer.sources?.[source] || 0, 0)
    if (available <= 0) {
      continue
    }
    const amount = Math.min(available, remaining)
    remaining = Math.round((remaining - amount) * 100) / 100
    plan.push({
      source,
      label: SOURCE_LABELS[source],
      amount,
      ...(source === 'planet_loans' ? { approximate: true } : {}),
    })
  }

  return plan
}

/**
 * Which flags to send back with the retried action. Only ask for what the
 * offer actually needs — an unnecessary `auto_finance` would be harmless but
 * makes the intent muddier in the server logs.
 */
export const acceptedRequest = (offer: PurchaseAssistOffer): PurchaseAssistRequest => ({
  auto_finance: offer.cash_to_raise > 0,
  auto_hire: offer.population_shortfall > 0,
})

/**
 * Whether the offer is worth showing at all. The server already refuses to
 * attach uncoverable offers, but a client-side guard keeps a stale or
 * hand-rolled payload from putting a dead "Accept" button on screen.
 */
export const isOfferActionable = (offer?: PurchaseAssistOffer | null): offer is PurchaseAssistOffer => {
  if (!offer || !offer.can_cover || offer.population_capacity_blocked) {
    return false
  }
  return offer.cash_to_raise > 0 || offer.population_shortfall > 0
}

/**
 * Headline for the popup — names the problem being solved, not the fix.
 */
export const offerTitle = (offer: PurchaseAssistOffer): string => {
  if (offer.population_shortfall > 0 && offer.cash_to_raise > 0) {
    return 'Not enough cash or crew'
  }
  if (offer.population_shortfall > 0) {
    return 'Not enough crew'
  }
  return 'Not enough cash'
}

/**
 * Label for the confirm button, so it reads as the action it performs.
 */
export const acceptLabel = (offer: PurchaseAssistOffer, actionLabel?: string): string => {
  const target = actionLabel ? ` & ${actionLabel}` : ''
  if (offer.population_shortfall > 0 && offer.cash_to_raise > 0) {
    return `Withdraw, Hire${target}`
  }
  if (offer.population_shortfall > 0) {
    return `Hire${target}`
  }
  return `Withdraw${target}`
}

/**
 * Human label for the thing being bought, used in the popup copy.
 * `undefined` for actions that aren't purchases.
 */
export const purchaseActionLabel = (action: string): string | undefined => {
  switch (action) {
    case 'purchase':
      return 'Buy Planet'
    case 'mine':
    case 'bulk_mine':
      return 'Buy Mine'
    case 'barrier':
    case 'barrier_modal':
    case 'bulk_barrier':
      return 'Buy Barrier'
    case 'tile':
      return 'Upgrade Tile'
    case 'strength':
      return 'Upgrade Strength'
    case 'submit_quest':
      return 'Submit Quest'
    default:
      return undefined
  }
}
